import React, { useState, useEffect, useRef, useMemo } from 'react';
import { requirementService } from '../../services/requirementService';
import ProductSelector from '../PurchaseOrders/ProductSelector';
import SupplierSelector from '../PurchaseOrders/SupplierSelector';
import UnitSelect from '../Common/UnitSelect';
import QuantityConversionHint from '../Common/QuantityConversionHint';
import LineItemField from '../Common/LineItemField';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { useProductCatalog } from '../../hooks/useProductCatalog';
import Toast from '../Common/Toast';
import ComprasItemsImportPreviewModal from '../Common/ComprasItemsImportPreviewModal';
import {
  downloadRequirementItemsTemplate,
  parseRequirementItemsExcel,
} from '../../utils/requirementItemsExcelImport';
import QuickProductLineBar from '../Common/QuickProductLineBar';
import ProductLineDefaultsBar from '../Common/ProductLineDefaultsBar';
import { emptyRequirementStagingItem } from '../../utils/quickProductLineFactories';
import {
  applyProductLineDefaults,
  buildProductLineSettings,
  emptyStagingWithDefaults,
  mergeProductLineDefaults,
} from '../../utils/productLineDefaults';

const emptyItem = (settings) => mergeProductLineDefaults({
  product_id: '',
  quantity: '',
  unit_of_measure: '',
  project_id: '',
  notes: '',
}, settings);

const stripImportMeta = ({ _label, _unitFormula, _unitIsCustom, ...item }) => item;

const RequirementForm = ({ isOpen, onClose, requirement = null, onSuccess, isPageMode = false, readOnly = false }) => {
  const { productConversionsEnabled, quickProductLineEnabled, productsProjectEnabled, loaded: moduleSettingsLoaded } = useModuleSettings();
  const [defaultLineProjectId, setDefaultLineProjectId] = useState('');
  const productLineSettings = useMemo(
    () => buildProductLineSettings({
      productsProjectEnabled,
      productsIgvEnabled: false,
      defaultProjectId: defaultLineProjectId,
    }),
    [productsProjectEnabled, defaultLineProjectId],
  );
  const [formData, setFormData] = useState({
    request_date: new Date().toISOString().split('T')[0],
    needed_by_date: '',
    with_supplier: false,
    supplier_id: '',
    notes: '',
    items: [{ product_id: '', quantity: '', unit_of_measure: '', project_id: '', notes: '' }],
  });
  const {
    products,
    getProduct: getCatalogProduct,
    mergeProducts,
    ensureByIds,
    searchProducts,
    resolveIdentifiers,
  } = useProductCatalog(null);
  const findProductById = (id, fallback = null) => getCatalogProduct(id, fallback);
  const productSelectorAsyncProps = {
    asyncSearch: true,
    knownProducts: products,
    onSearchProducts: searchProducts,
    onEnsureProduct: (id) => ensureByIds([id]),
  };
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [importingItems, setImportingItems] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const itemsImportFileRef = useRef(null);
  const hasLoadedFormData = useRef(false);
  const [stagingItem, setStagingItem] = useState(() => emptyRequirementStagingItem());
  const [stagingError, setStagingError] = useState('');
  const stagingProductSelectorRef = useRef(null);

  useEffect(() => {
    if (!productsProjectEnabled) return;
    setStagingItem((prev) => mergeProductLineDefaults(prev, productLineSettings));
  }, [productLineSettings, productsProjectEnabled]);

  useEffect(() => {
    if ((isOpen || isPageMode) && !hasLoadedFormData.current) {
      hasLoadedFormData.current = true;
      loadFormData();
    }
  }, [isOpen, isPageMode]);

  useEffect(() => {
    const ids = (formData.items || []).map((item) => item.product_id).filter(Boolean);
    if (ids.length > 0) ensureByIds(ids).catch(() => {});
  }, [formData.items, ensureByIds]);

  useEffect(() => {
    if (requirement?.id) {
      setFormData({
        request_date: requirement.request_date?.split?.('T')?.[0] || requirement.request_date || '',
        needed_by_date: requirement.needed_by_date?.split?.('T')?.[0] || requirement.needed_by_date || '',
        with_supplier: Boolean(requirement.supplier_id),
        supplier_id: requirement.supplier_id ? String(requirement.supplier_id) : '',
        notes: requirement.notes || '',
        items: (requirement.items || []).map((item) => ({
          product_id: String(item.product_id),
          quantity: String(item.quantity),
          unit_of_measure: item.unit_of_measure || '',
          project_id: item.project_id ? String(item.project_id) : '',
          notes: item.notes || '',
        })),
      });
      mergeProducts((requirement.items || []).map((item) => item.product).filter(Boolean));
    }
  }, [requirement, mergeProducts]);

  const loadFormData = async () => {
    try {
      const res = await requirementService.getFormData();
      if (res.success) {
        setUnits(res.data?.units || []);
        setProjects(res.data?.projects || []);
        setSuppliers(res.data?.suppliers || []);
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar datos del formulario', type: 'error' });
    }
  };

  const validate = () => {
    if (!formData.request_date) {
      setToast({ isOpen: true, message: 'La fecha del requerimiento es obligatoria', type: 'error' });
      return false;
    }
    if (formData.with_supplier && !formData.supplier_id) {
      setToast({ isOpen: true, message: 'Seleccione un proveedor', type: 'error' });
      return false;
    }
    for (const item of formData.items) {
      if (!item.product_id || !item.quantity || parseFloat(item.quantity) <= 0) {
        setToast({ isOpen: true, message: 'Complete producto y cantidad en todas las líneas', type: 'error' });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly || !validate()) return;

    setLoading(true);
    try {
      const payload = {
        request_date: formData.request_date,
        needed_by_date: formData.needed_by_date || null,
        with_supplier: formData.with_supplier === true,
        notes: formData.notes,
        items: formData.items.map((item) => ({
          product_id: parseInt(item.product_id, 10),
          quantity: parseFloat(item.quantity),
          unit_of_measure: item.unit_of_measure || null,
          project_id: item.project_id ? parseInt(item.project_id, 10) : null,
          notes: item.notes || null,
        })),
      };

      if (formData.with_supplier) {
        payload.supplier_id = parseInt(formData.supplier_id, 10);
      }

      const response = requirement?.id
        ? await requirementService.updateRequirement(requirement.id, payload)
        : await requirementService.createRequirement(payload);

      if (response.success) {
        setToast({ isOpen: true, message: response.message || 'Guardado correctamente', type: 'success' });
        setTimeout(() => onSuccess(), 800);
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || err.message || 'Error al guardar',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadItemsTemplate = () => {
    downloadRequirementItemsTemplate();
  };

  const handlePickItemsImport = () => {
    itemsImportFileRef.current?.click();
  };

  const handleItemsImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const nameOk = /\.xlsx$/i.test(file.name || '') || (file.type || '').includes('spreadsheetml');
    if (!nameOk) {
      setToast({
        isOpen: true,
        message: 'Use un archivo Excel .xlsx (misma estructura que la plantilla descargada).',
        type: 'error',
      });
      return;
    }
    if (!products.length) {
      setToast({
        isOpen: true,
        message: 'Espere a que carguen los productos del formulario.',
        type: 'warning',
      });
      return;
    }
    setImportingItems(true);
    try {
      const { items: parsed, errors, warnings } = await parseRequirementItemsExcel(file, {
        products,
        units,
        projects,
        productConversionsEnabled,
      });
      if (parsed.length === 0 && !errors.length) {
        setToast({ isOpen: true, message: 'No se importó ninguna fila con datos.', type: 'warning' });
        return;
      }
      setImportPreview({
        fileName: file.name,
        items: parsed,
        errors: errors || [],
        warnings: warnings || [],
      });
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'Error al leer el archivo Excel.',
        type: 'error',
      });
    } finally {
      setImportingItems(false);
    }
  };

  const handleConfirmImportPreview = (confirmedRows) => {
    const source = confirmedRows || importPreview?.items || [];
    if (!source.length) {
      setImportPreview(null);
      return;
    }
    const toAdd = source.map(stripImportMeta).map((item) => ({
      product_id: String(item.product_id),
      quantity: String(item.quantity),
      unit_of_measure: item.unit_of_measure || '',
      project_id: item.project_id ? String(item.project_id) : '',
      notes: item.notes || '',
    }));
    const hasExisting = formData.items.some((i) => i.product_id);
    setFormData((prev) => ({
      ...prev,
      items: hasExisting ? [...prev.items.filter((i) => i.product_id), ...toAdd] : toAdd,
    }));
    setImportPreview(null);
    setToast({
      isOpen: true,
      message: `Se añadieron ${toAdd.length} línea(s) al requerimiento.`,
      type: 'success',
    });
  };

  const updateItem = (index, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      if (field === 'product_id') {
        const product = findProductById(value);
        if (product?.unit_of_measure) {
          items[index].unit_of_measure = product.unit_of_measure;
        }
        const defaults = applyProductLineDefaults(productLineSettings);
        if (defaults.project_id !== undefined) {
          items[index].project_id = defaults.project_id;
        }
      }
      return { ...prev, items };
    });
  };

  useEffect(() => {
    if (!moduleSettingsLoaded || requirement?.id || readOnly) return;
    setFormData((prev) => {
      if (quickProductLineEnabled) {
        const hasData = prev.items.some((i) => i.product_id);
        return hasData ? prev : { ...prev, items: [] };
      }
      if (prev.items.length === 0) {
        return { ...prev, items: [emptyItem(productLineSettings)] };
      }
      return prev;
    });
  }, [moduleSettingsLoaded, quickProductLineEnabled, requirement?.id, readOnly]);

  const handleStagingChange = (field, value) => {
    setStagingError('');
    setStagingItem((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'product_id') {
        const product = findProductById(value);
        if (product?.unit_of_measure) {
          next.unit_of_measure = product.unit_of_measure;
        }
        const defaults = applyProductLineDefaults(productLineSettings);
        if (defaults.project_id !== undefined) {
          next.project_id = defaults.project_id;
        }
      }
      return next;
    });
  };

  const handleQuickAddItem = () => {
    if (!stagingItem.product_id) {
      setStagingError('Seleccione un producto');
      return;
    }
    const qty = parseFloat(stagingItem.quantity);
    if (!qty || qty <= 0) {
      setStagingError('La cantidad debe ser mayor a 0');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { ...stagingItem, quantity: String(stagingItem.quantity) }],
    }));
    setStagingItem(emptyStagingWithDefaults(emptyRequirementStagingItem, productLineSettings));
    setStagingError('');
    window.setTimeout(() => stagingProductSelectorRef.current?.focus?.(), 0);
  };

  if (!isOpen && !isPageMode) return null;

  return (
    <>
      <form onSubmit={handleSubmit} className={isPageMode ? 'space-y-6' : 'px-6 py-4 space-y-6'}>
        <fieldset disabled={readOnly} className="space-y-6 border-0 p-0 m-0">
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Datos generales</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de requerimiento</label>
              <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="requirement_type"
                    checked={!formData.with_supplier}
                    onChange={() =>
                      setFormData((p) => ({ ...p, with_supplier: false, supplier_id: '' }))
                    }
                    className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">Sin proveedor (interno)</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="requirement_type"
                    checked={formData.with_supplier === true}
                    onChange={() => setFormData((p) => ({ ...p, with_supplier: true }))}
                    className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">Con proveedor</span>
                </label>
              </div>
            </div>

            {formData.with_supplier && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor *</label>
                <SupplierSelector
                  value={formData.supplier_id}
                  onChange={(id) =>
                    setFormData((p) => ({ ...p, supplier_id: id ? String(id) : '' }))
                  }
                  suppliers={suppliers}
                  disabled={readOnly}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha del requerimiento *</label>
              <input
                type="date"
                value={formData.request_date}
                onChange={(e) => setFormData((p) => ({ ...p, request_date: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha requerida</label>
              <input
                type="date"
                value={formData.needed_by_date}
                onChange={(e) => setFormData((p) => ({ ...p, needed_by_date: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>
        </fieldset>

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-center mb-5 pb-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              Productos
            </h3>
            {!readOnly && (
              <div className="flex flex-wrap items-center gap-2">
                {!quickProductLineEnabled && (
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, items: [...p.items, emptyItem(productLineSettings)] }))}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                  >
                    + Agregar línea
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleDownloadItemsTemplate}
                  className="px-3 py-2 border border-gray-300 bg-white text-gray-800 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Descargar plantilla
                </button>
                <input
                  ref={itemsImportFileRef}
                  type="file"
                  accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={handleItemsImportFile}
                />
                <button
                  type="button"
                  onClick={handlePickItemsImport}
                  disabled={importingItems}
                  className="px-3 py-2 border border-teal-300 bg-teal-50 text-teal-900 rounded-lg hover:bg-teal-100 text-sm font-medium disabled:opacity-50"
                >
                  {importingItems ? 'Importando…' : 'Importar Excel'}
                </button>
              </div>
            )}
          </div>

          {!readOnly && productsProjectEnabled && (
            <ProductLineDefaultsBar
              productsProjectEnabled={productsProjectEnabled}
              productsIgvEnabled={false}
              projects={projects}
              defaultProjectId={defaultLineProjectId}
              onDefaultProjectIdChange={setDefaultLineProjectId}
              className="mb-4"
            />
          )}

          {!readOnly && quickProductLineEnabled && (
            <QuickProductLineBar onAdd={handleQuickAddItem} error={stagingError}>
              <LineItemField label="Producto" required labelClassName="text-gray-600" className="flex-[2] min-w-[11rem]">
                <ProductSelector
                  ref={stagingProductSelectorRef}
                  value={stagingItem.product_id}
                  onChange={(v) => handleStagingChange('product_id', v)}
                  {...productSelectorAsyncProps}
                  compact
                />
              </LineItemField>
              <LineItemField label="Proyecto" labelClassName="text-gray-600" className="flex-1 min-w-[8rem]">
                <select
                  value={stagingItem.project_id}
                  onChange={(e) => handleStagingChange('project_id', e.target.value)}
                  className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">—</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code && p.name ? `${p.code} - ${p.name}` : p.code || p.name}
                    </option>
                  ))}
                </select>
              </LineItemField>
              <LineItemField label="Cant." required labelClassName="text-gray-600" className="w-[4.5rem] flex-shrink-0">
                <input
                  type="number"
                  min="0.001"
                  step="any"
                  value={stagingItem.quantity}
                  onChange={(e) => handleStagingChange('quantity', e.target.value)}
                  className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="0"
                />
              </LineItemField>
              <LineItemField label="Unidad" labelClassName="text-gray-600" className="w-24 min-w-[6rem] flex-shrink-0">
                <UnitSelect
                  value={stagingItem.unit_of_measure || ''}
                  onChange={(v) => handleStagingChange('unit_of_measure', v)}
                  units={units}
                  product={findProductById(stagingItem.product_id, stagingItem.product)}
                  compact
                />
              </LineItemField>
            </QuickProductLineBar>
          )}

          <div className="space-y-1.5">
            {formData.items.map((item, index) => (
              <div key={index} className="bg-gray-50 border border-gray-100 rounded-md px-2 py-2">
                <div className="flex flex-nowrap items-start gap-1.5 min-w-0 w-full">
                  <LineItemField
                    label="Producto"
                    required
                    labelClassName="text-gray-600"
                    className="flex-[2] min-w-[11rem]"
                  >
                    <ProductSelector
                      value={item.product_id}
                      onChange={(v) => updateItem(index, 'product_id', v)}
                      {...productSelectorAsyncProps}
                      compact
                      disabled={readOnly}
                    />
                  </LineItemField>
                  <LineItemField label="Proyecto" labelClassName="text-gray-600" className="flex-1 min-w-[8rem]">
                    <select
                      value={item.project_id}
                      onChange={(e) => updateItem(index, 'project_id', e.target.value)}
                      disabled={readOnly}
                      className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100"
                    >
                      <option value="">—</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code && p.name ? `${p.code} - ${p.name}` : p.code || p.name}
                        </option>
                      ))}
                    </select>
                  </LineItemField>
                  <LineItemField
                    label="Cant."
                    required
                    labelClassName="text-gray-600"
                    className="w-[4.5rem] flex-shrink-0"
                    hint={
                      <QuantityConversionHint
                        product={findProductById(item.product_id, item.product)}
                        unitCode={item.unit_of_measure}
                        quantity={item.quantity}
                      />
                    }
                  >
                    <input
                      type="number"
                      min="0.001"
                      step="any"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      disabled={readOnly}
                      className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100"
                      placeholder="0"
                    />
                  </LineItemField>
                  <LineItemField label="Unidad" labelClassName="text-gray-600" className="w-24 min-w-[6rem] flex-shrink-0">
                    <UnitSelect
                      value={item.unit_of_measure || ''}
                      onChange={(v) => updateItem(index, 'unit_of_measure', v)}
                      units={units}
                      product={findProductById(item.product_id, item.product)}
                      compact
                      disabled={readOnly}
                    />
                  </LineItemField>
                  {!readOnly && formData.items.length > 1 && (
                    <LineItemField hideLabel className="w-8 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((p) => ({
                            ...p,
                            items: p.items.filter((_, i) => i !== index),
                          }))
                        }
                        className="h-[34px] w-full flex items-center justify-center text-red-500 rounded hover:bg-red-50 hover:text-red-700 transition-colors"
                        title="Quitar línea"
                        aria-label="Quitar línea"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </LineItemField>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {!readOnly && (
          <div className="flex gap-3 justify-end pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg text-gray-700">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {loading ? 'Guardando...' : requirement?.id ? 'Actualizar' : 'Crear requerimiento'}
            </button>
          </div>
        )}
      </form>
      <ComprasItemsImportPreviewModal
        isOpen={Boolean(importPreview)}
        onClose={() => setImportPreview(null)}
        onConfirm={handleConfirmImportPreview}
        fileName={importPreview?.fileName}
        items={importPreview?.items || []}
        errors={importPreview?.errors || []}
        warnings={importPreview?.warnings || []}
        projects={projects}
        products={products}
        units={units}
        hidePricing
        showNotes
        confirmLabel="Añadir al requerimiento"
        entityLabel="el requerimiento"
      />
      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, isOpen: false })} />
    </>
  );
};

export default RequirementForm;




